import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { COMMON_ERROR } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'

import { clearAddMailboxModalValues } from './AddMailbox/actions'
import tableRowEpics from './TableRow/epics'
import { TAGS_FORM_SUBMIT_ASYNC } from './TagsForm/constants'
import tagsFormEpics from './TagsForm/epics'
import { UPDATE_DATA_REQUEST_ASYNC } from './VipDiscovery/constants'
import vipDiscoveryEpics from './VipDiscovery/epics'
import {
  createComplianceReportError,
  createComplianceReportSuccess,
  fetchData,
  fetchDataError,
  fetchDataSuccess,
  fetchManagementTagsError,
  fetchManagementTagsSuccess,
  fetchProfilesSuggestionsError,
  fetchProfilesSuggestionsSuccess,
  statusesToggleError,
  statusesToggleSuccess,
} from './actions'
import {
  ADD_ASYNC,
  CREATE_COMPLIANCE_REPORT_ASYNC,
  DATA_REQUEST_ASYNC,
  FETCH_MANAGEMENT_TAGS_ASYNC,
  FETCH_PROFILES_SUGGESTIONS_ASYNC,
  PAGINATE_CHANGE,
  PROTECTED_FILTER_CHANGE,
  RISK_LEVELS_FILTER_CHANGE,
  SEARCH_CHANGE,
  SEGMENTS_FILTER_CHANGE,
  SORT_PROPERTY_CHANGE,
  STATUSES_TOGGLE_ASYNC,
  TAGS_FILTER_CHANGE,
  TYPE_FILTER_CHANGE,
} from './constants'
import {
  selectComplianceReportData,
  selectManagementTagsPage,
  selectManagementTagsSearch,
} from './selectors'

const fetchDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      SORT_PROPERTY_CHANGE,
      TYPE_FILTER_CHANGE,
      PROTECTED_FILTER_CHANGE,
      TAGS_FILTER_CHANGE,
      SEGMENTS_FILTER_CHANGE,
      RISK_LEVELS_FILTER_CHANGE,
      PAGINATE_CHANGE,
      SEARCH_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      UPDATE_DATA_REQUEST_ASYNC.SUCCESS,
      STATUSES_TOGGLE_ASYNC.SUCCESS,
      TAGS_FORM_SUBMIT_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.mailboxes.management)
      return true
    }),
    switchMap(() =>
      api.mailboxes
        .get(
          state$.value.mailboxes.management.page || 1,
          state$.value.mailboxes.management.sortProperty,
          state$.value.mailboxes.management.typeFilter,
          state$.value.mailboxes.management.protectedFilter,
          state$.value.mailboxes.management.tagsFilter.map((elem) => elem.value).join(','),
          state$.value.mailboxes.management.segmentsFilter.map((elem) => elem.value).join(','),
          state$.value.mailboxes.management.riskLevelsFilter.map((elem) => elem.value).join(','),
          state$.value.mailboxes.management.search,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => fetchDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchDataError, error))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const statusesToggleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(STATUSES_TOGGLE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.mailboxes
        .toggleStatuses(state$.value.partnerOptions.selectedCompany?.value, {
          ids: Object.keys(state$.value.mailboxes.management.selectedMailboxesMap),
          active: action.payload.status,
          selectAll: state$.value.mailboxes.management.selectAll,
          typeFilter: state$.value.mailboxes.management.typeFilter,
          protectedFilter: state$.value.mailboxes.management.protectedFilter,
          tagsFilter: state$.value.mailboxes.management.selectAll
            ? state$.value.mailboxes.management.tagsFilter.map((elem) => elem.value).join(',')
            : '',
          segmentsFilter: state$.value.mailboxes.management.selectAll
            ? state$.value.mailboxes.management.segmentsFilter.map((elem) => elem.value).join(',')
            : '',
          riskLevelsFilter: state$.value.mailboxes.management.selectAll
            ? state$.value.mailboxes.management.riskLevelsFilter.map((elem) => elem.value).join(',')
            : '',
          search: state$.value.mailboxes.management.search,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              statusesToggleSuccess(response || {}),
              addToast({
                message: response.message || 'Statuses updated successfully!',
                type: response.success ? 'success' : 'error',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(statusesToggleError, error),
              addToast({
                message: error.response || COMMON_ERROR,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(STATUSES_TOGGLE_ASYNC.CANCEL)))
        )
    )
  )

const addMailboxEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value
      const formValues = state.form.AddMailboxForm.values
      const { selectedTitle, selectedDepartment, selectedAllTags } = state.mailboxes.addMailbox

      const payload = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        phone: formValues.phone,
        ...(selectedTitle && { title: selectedTitle }), // Add title only if it's not null
        ...(selectedDepartment && { department: selectedDepartment }), // Add department only if it's not null
        ...(selectedAllTags?.length && { tags: selectedAllTags }), // Add tags only if it's not null
      }

      return api.mailboxes.add(state$.value.partnerOptions.selectedCompany?.value, payload).pipe(
        mergeMap(({ response }) => {
          if (response.success) {
            return of(
              fetchData(),
              clearAddMailboxModalValues(),
              addToast({
                message: response.message || 'Mailbox added successfully!',
              })
            )
          }
          return of(
            addToast({
              message: response.message || COMMON_ERROR,
              type: 'error',
            })
          )
        }),
        catchError((error) => of(epicsErrorHandler(ADD_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(ADD_ASYNC.CANCEL)))
      )
    })
  )

const fetchProfilesSuggestionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_PROFILES_SUGGESTIONS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.mailboxes
        .getEmails(state$.value.partnerOptions.selectedCompany?.value, action.payload.search, false)
        .pipe(
          map((response) => fetchProfilesSuggestionsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchProfilesSuggestionsError, error))),
          takeUntil(action$.pipe(ofType(FETCH_PROFILES_SUGGESTIONS_ASYNC.CANCEL)))
        )
    )
  )

const createComplianceReportEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_COMPLIANCE_REPORT_ASYNC.INITIATE),
    mergeMap((action) => {
      const complianceReportData = selectComplianceReportData(state$.value)

      return api.mailboxes
        .createComplianceReport(state$.value.partnerOptions.selectedCompany?.value, {
          reportType: complianceReportData.reportType,
          outputFormat: complianceReportData.exportFormatFilter,
          startDate: complianceReportData.startDate,
          endDate: complianceReportData.endDate,
          recipients: complianceReportData.recipients,
          message: complianceReportData.message,
        })
        .pipe(
          map((response) => createComplianceReportSuccess(response)),
          catchError((error) => of(epicsErrorHandler(createComplianceReportError, error))),
          takeUntil(action$.pipe(ofType(CREATE_COMPLIANCE_REPORT_ASYNC.CANCEL)))
        )
    })
  )

const fetchTagsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_MANAGEMENT_TAGS_ASYNC.INITIATE,
      UPDATE_DATA_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      TAGS_FORM_SUBMIT_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return window.location.href.indexOf(URL_CONST.mailboxes.self) !== -1
      return true
    }),
    mergeMap(() =>
      api.mailboxes
        .getTags({
          companyId: selectCompanyFilter(state$.value)?.value,
          allTags: false,
          page: selectManagementTagsPage(state$.value),
          search: selectManagementTagsSearch(state$.value),
        })
        .pipe(
          map((response) => fetchManagementTagsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchManagementTagsError, error))),
          takeUntil(action$.pipe(ofType(FETCH_MANAGEMENT_TAGS_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  tableRowEpics,
  tagsFormEpics,
  vipDiscoveryEpics,
  fetchDataEpic,
  statusesToggleEpic,
  addMailboxEpic,
  fetchProfilesSuggestionsEpic,
  createComplianceReportEpic,
  fetchTagsEpic
)
